import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import type { APIError } from "simplydo/core/api";
import {
  type IdeaUpdatePayload,
  diffIdeaUpdatePayload,
  mergeIdeaUpdatePayload,
  mergePendingIdeaPayloads,
  reducePath,
  removeSavedIdeaEdits,
} from "simplydo/core/ideaUpdates";
import { initUseQuery, queryKey } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";
import {
  useWebsocketReconnect,
  useWebsocketRoom,
  useWebsocketStatus,
} from "simplydo/react/hooks/useWebsocket";

import api, { websocketApi } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type {
  IdeaCollaboratorUser,
  IdeaDetail,
  IdeaEditorState,
  IdeaExternalChanges,
  UpdateIdeaFn,
  UseIdeaEditorReturn,
} from "../types";
import { useIdeaDraft, useIdeaEdits, useIdeaIdState } from "./useIdea";

export { reducePath };

const maxHistoryLength = 500;

/**
 * Presence indicators are shown per idea field (FieldHeader), so nested
 * edit paths like ["templated", fieldId, "value"] must collapse to the
 * field itself: ["templated", fieldId].
 */
const presencePathFor = (fieldId: string[]): string[] => {
  if (!fieldId.length) return [];
  if (fieldId[0] === "templated" || fieldId[0] === "assessment") {
    return fieldId.slice(0, Math.min(2, fieldId.length));
  }
  return fieldId.slice(0, 1);
};

const resolveEditPath = (
  fieldId: string[] | string | undefined,
  templated?: boolean,
): string[] => {
  if (Array.isArray(fieldId)) return fieldId;
  if (typeof fieldId === "string" && fieldId) {
    return templated === false ? [fieldId] : ["templated", fieldId];
  }
  return [];
};

const isCollaboratorUser = (value: unknown): value is IdeaCollaboratorUser =>
  !!value &&
  typeof value === "object" &&
  typeof (value as IdeaCollaboratorUser)._id === "string";

/** Coerce legacy array / nested-path presence data into a userId → user map. */
const normalizeEditors = (
  existing: unknown,
): Record<string, IdeaCollaboratorUser> => {
  if (!existing) return {};
  if (Array.isArray(existing)) {
    return Object.fromEntries(
      existing.filter(isCollaboratorUser).map((user) => [user._id, user]),
    );
  }
  if (typeof existing !== "object") return {};

  const result: Record<string, IdeaCollaboratorUser> = {};
  for (const value of Object.values(existing as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      for (const user of value) {
        if (isCollaboratorUser(user)) result[user._id] = user;
      }
    } else if (isCollaboratorUser(value)) {
      result[value._id] = value;
    }
  }
  return result;
};

const removeUsersFromExternalChanges = (
  changes: IdeaExternalChanges | undefined,
  shouldRemove: (userId: string) => boolean,
): IdeaExternalChanges => {
  if (!changes) return {};

  const isRemoved = (value: unknown) =>
    isCollaboratorUser(value) && shouldRemove(value._id);

  const cleanNode = (node: unknown): unknown => {
    if (!node || typeof node !== "object") return node;
    if (Array.isArray(node)) {
      return node.filter((entry) => !isRemoved(entry));
    }

    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(
      node as Record<string, unknown>,
    )) {
      if (isRemoved(value)) {
        continue;
      }
      const nextValue = cleanNode(value);
      if (Array.isArray(nextValue) && nextValue.length === 0) continue;
      if (
        nextValue &&
        typeof nextValue === "object" &&
        !Array.isArray(nextValue) &&
        Object.keys(nextValue as object).length === 0
      ) {
        continue;
      }
      cleaned[key] = nextValue;
    }
    return cleaned;
  };

  return cleanNode(changes) as IdeaExternalChanges;
};

type IdeaWebsocketEvent = {
  event: string;
  fieldId: string[] | string;
  fieldData: unknown;
  templated?: boolean;
  user: IdeaCollaboratorUser;
};

type IdeaHistorySnapshot = {
  templated?: OpenAPI.Schemas["Idea"]["templated"];
  name?: OpenAPI.Schemas["Idea"]["name"];
  coverImage?: OpenAPI.Schemas["Idea"]["coverImage"];
};

export const useIdeaEditor = (): UseIdeaEditorReturn => {
  const [ideaId] = useIdeaIdState();

  const [historyState, setHistoryState] = useState<{
    undo: IdeaHistorySnapshot[];
    redo: IdeaHistorySnapshot[];
  }>({ undo: [], redo: [] });
  const [externalChanges, setExternalChanges] = useState<IdeaExternalChanges>();
  const [unsaved, setUnsaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | "">("");
  const editTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ideaAtStartOfUpdate = useRef<OpenAPI.Schemas["Idea"] | null>(null);
  const currentSave = useRef("");
  const draft = useIdeaDraft();
  const edits = useIdeaEdits(ideaId);
  const user = useAppSelector((state) => state.user);
  const userId = user?._id;
  const { t } = useTranslation();

  const ideaQuery = useQuery(
    initUseQuery(api.ideas.get, [ideaId ?? ""], {
      isEnabled: !!ideaId,
      placeholderData: keepPreviousData,
    }),
  );
  const idea =
    ideaQuery.data && mergePendingIdeaPayloads(ideaQuery.data, edits);

  const ideaState: IdeaEditorState = {
    unsaved,
    isSaving,
    savedAt,
    notFound: ideaQuery.isError,
    errorMessage: (ideaQuery.error as APIError | null)?.message ?? "",
  };

  const isCollaborator = !!idea?.authors?.some(
    (author) => author._id === userId,
  );
  const ideaReady = !!idea && idea._id === ideaId;

  const websocketStatus = useWebsocketStatus(websocketApi);

  const reloadIdea = useCallback(() => {
    api.invalidateQueries(api.ideas.get, [ideaId ?? ""]);
  }, [ideaId]);

  useEffect(() => {
    setHistoryState({ undo: [], redo: [] });
    setUnsaved(false);
    setSavedAt("");
    setExternalChanges(undefined);
    ideaAtStartOfUpdate.current = null;
    currentSave.current = "";
    if (editTimeout.current !== null) {
      clearTimeout(editTimeout.current);
      editTimeout.current = null;
    }
    // A save still in flight for this idea is ignored once we switch away,
    // so its cache entry has to be refetched when it is opened again
    return () => {
      if (ideaId) {
        api.invalidateQueries(api.ideas.get, [ideaId]);
      }
    };
  }, [ideaId]);

  const flushPendingChanges = useCallback(() => {
    const toSend = draft.get();
    if (!Object.keys(toSend).length || !ideaId) {
      return;
    }

    const fetchId = uuidv4();
    currentSave.current = fetchId;
    const savedAtTime = new Date();

    setIsSaving(true);
    api.ideas.update(
      ideaId,
      toSend,
      (serverResponse: OpenAPI.PUT<"/ideas/{id}">["response"]) => {
        const serverData = (serverResponse ?? {}) as Partial<IdeaDetail>;
        const templated = serverData.templated;
        const serverChanges = { ...serverData };
        delete serverChanges.templated;
        delete serverChanges.name;
        delete serverChanges.ownerIdeaTemplate;
        if (currentSave.current !== fetchId) {
          return;
        }

        let savedIdea: IdeaDetail | undefined;
        api.setQueryData(api.ideas.get, [ideaId ?? ""], (prev) => {
          if (!prev) {
            return prev;
          }
          savedIdea = Object.assign(
            mergePendingIdeaPayloads(prev, toSend),
            serverChanges,
            templated ? { templated } : {},
          );
          return savedIdea;
        });
        draft.set(removeSavedIdeaEdits(draft.get(), toSend));
        if (savedIdea) {
          const { name, coverImage } = savedIdea;
          util.onUpdateIdea(savedIdea, {
            name,
            coverImage,
            templated: savedIdea.templated,
          });
        }
        setIsSaving(false);
        if (Object.keys(draft.get()).length > 0) {
          setUnsaved(true);
        } else {
          setUnsaved(false);
          setSavedAt(savedAtTime);
        }
      },
      (err) => {
        if (currentSave.current !== fetchId) {
          return;
        }
        if (err.errorCode === "ideaIsSubmitted") {
          toast.error(
            `This ${t("generic.idea")} cannot be edited because it has been submitted.`,
          );

          draft.set({});
          setIsSaving(false);
          setUnsaved(false);
          reloadIdea();
        } else {
          toast.error(err.message);
          setIsSaving(false);
          setUnsaved(true);
        }
      },
    );
  }, [ideaId, draft, t, reloadIdea]);

  const saveIdeaPartial = useCallback(
    (changes: IdeaUpdatePayload) => {
      draft.set(mergePendingIdeaPayloads(draft.get(), changes));
      setUnsaved(true);
      flushPendingChanges();
    },
    [draft, flushPendingChanges],
  );

  const asHistoryState = useCallback(
    (
      historyIdea: Pick<
        OpenAPI.Schemas["Idea"],
        "templated" | "name" | "coverImage"
      >,
    ) => ({
      templated: historyIdea.templated,
      name: historyIdea.name,
      coverImage: historyIdea.coverImage,
    }),
    [],
  );

  const addHistoryEntry = useCallback(
    (updatedIdea: Partial<OpenAPI.Schemas["Idea"]>) => {
      setHistoryState((prevHistory) => {
        const slicedUndoHistory = prevHistory.undo.slice(
          Math.max(prevHistory.undo.length - maxHistoryLength, 0),
        );
        return {
          ...prevHistory,
          undo: [...slicedUndoHistory, asHistoryState(updatedIdea)],
        };
      });
    },
    [asHistoryState],
  );

  const undoChanges = useCallback(() => {
    if (historyState.undo.length === 0 || !idea) {
      return;
    }
    const undoItem = historyState.undo[historyState.undo.length - 1];
    if (!undoItem) {
      return;
    }
    const partial = diffIdeaUpdatePayload(idea, undoItem);
    if (Object.keys(partial).length > 0) {
      saveIdeaPartial(partial);
    }
    setHistoryState((prevHistory) => ({
      redo: [...prevHistory.redo, asHistoryState(idea)],
      undo: prevHistory.undo.slice(0, -1),
    }));
  }, [historyState.undo, idea, saveIdeaPartial, asHistoryState]);

  const redoChanges = useCallback(() => {
    if (historyState.redo.length === 0 || !idea) {
      return;
    }
    const redoItem = historyState.redo[historyState.redo.length - 1];
    if (!redoItem) {
      return;
    }
    const partial = diffIdeaUpdatePayload(idea, redoItem);
    if (Object.keys(partial).length > 0) {
      saveIdeaPartial(partial);
    }
    setHistoryState((prevHistory) => ({
      redo: prevHistory.redo.slice(0, -1),
      undo: [...prevHistory.undo, asHistoryState(idea)],
    }));
  }, [historyState.redo, idea, saveIdeaPartial, asHistoryState]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === 90 && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          redoChanges();
        } else {
          undoChanges();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [redoChanges, undoChanges]);

  /**
   * `update` can either be:
   *   - data which will be inserted into the idea at the path
   *   - a function which will be called with the current idea and
   *     should only return the data to be inserted at the path
   */
  const updateIdea: UpdateIdeaFn = useCallback(
    (update: unknown, path: string[], suppressSave = false) => {
      if (!ideaId) {
        return;
      }
      if (update === true && path.includes("isSubmitted")) {
        websocketApi.signal({ type: "idea", id: ideaId }, "ideaSubmitted");
      }

      if (suppressSave) {
        let updatedIdea: OpenAPI.Schemas["Idea"] | undefined;
        api.setQueryData(api.ideas.get, [ideaId], (prev) => {
          if (!prev) {
            return prev;
          }
          updatedIdea = reducePath(
            prev,
            update as Partial<IdeaDetail>,
            path,
          ) as IdeaDetail;
          return updatedIdea;
        });
        if (updatedIdea) {
          util.onUpdateIdea(updatedIdea, update, path);
        }
        return;
      }

      if (!ideaAtStartOfUpdate.current) {
        const cachedIdea = api.queryClient.getQueryData<IdeaDetail>(
          queryKey(api.ideas.get, [ideaId]),
        );
        ideaAtStartOfUpdate.current = cachedIdea
          ? mergePendingIdeaPayloads(cachedIdea, draft.get())
          : null;
      }
      draft.set(mergeIdeaUpdatePayload(draft.get(), update, path));
      setUnsaved(true);

      if (editTimeout.current !== null) {
        clearTimeout(editTimeout.current);
        editTimeout.current = null;
      }

      editTimeout.current = setTimeout(() => {
        flushPendingChanges();
        websocketApi.signal({ type: "idea", id: ideaId }, "fieldEdited", {
          fieldId: path,
          fieldData: update,
        });
        setHistoryState((prevHistory) => ({ ...prevHistory, redo: [] }));
        if (ideaAtStartOfUpdate.current) {
          addHistoryEntry(asHistoryState(ideaAtStartOfUpdate.current));
        }

        ideaAtStartOfUpdate.current = null;
        editTimeout.current = null;
      }, 400);
    },
    [ideaId, draft, asHistoryState, addHistoryEntry, flushPendingChanges],
  );

  // The server decides whether we are a visible participant (collaborators) or
  // a silent observer, re-joining lets it re-evaluate when that changes
  const { members } = useWebsocketRoom(
    websocketApi,
    ideaId && ideaReady ? { type: "idea", id: ideaId } : null,
    (message) => {
      const data = message as IdeaWebsocketEvent;
      const { event, fieldData } = data;
      // Discard any messages that were sent by the current user
      if (data.user && data.user._id === userId) {
        return;
      }

      switch (event) {
        case "ideaSubmitted":
          reloadIdea();
          toast(
            `${data.user.profile?.fullName || data.user.name || "A collaborator"} submitted the ${t("generic.idea")}.`,
          );
          break;
        case "fieldEdited": {
          const editPath = resolveEditPath(data.fieldId, data.templated);
          if (!editPath.length) break;

          const presencePath = presencePathFor(editPath);
          setExternalChanges((prevChanges) => {
            // Only the most recently edited field should show this user
            const cleared = removeUsersFromExternalChanges(
              prevChanges,
              (id) => id === data.user._id,
            );
            const externalChanges = { ...cleared } as Record<string, unknown>;
            let change: Record<string, unknown> = externalChanges;

            presencePath.forEach((field, i) => {
              if (i < presencePath.length - 1) {
                const nested = {
                  ...((change[field] as Record<string, unknown>) || {}),
                };
                change[field] = nested;
                change = nested;
                return;
              }

              const editors = normalizeEditors(change[field]);
              editors[data.user._id] = {
                ...data.user,
                isEditing: { fieldId: editPath, fieldData },
              };
              change[field] = editors;
            });

            return externalChanges as IdeaExternalChanges;
          });
          updateIdea(fieldData, editPath, true);
          break;
        }
        default:
          break;
      }
    },
    { rejoinKey: isCollaborator },
  );
  const otherUsers: Record<string, IdeaCollaboratorUser> = Object.fromEntries(
    members
      .filter((member) => member._id !== userId)
      .map((member) => [member._id, member]),
  );

  // Changes made while disconnected were missed
  useWebsocketReconnect(websocketApi, () => {
    setExternalChanges(undefined);
    reloadIdea();
  });

  return {
    ideaState,
    updateIdea,
    reloadIdea,
    undoChanges,
    redoChanges,
    canUndo: historyState.undo.length > 0,
    canRedo: historyState.redo.length > 0,
    otherUsers,
    websocketConnected: websocketStatus !== "disconnected",
    // Editing indicators of collaborators who have since left are dropped
    externalChanges: removeUsersFromExternalChanges(
      externalChanges,
      (id) => !otherUsers[id],
    ),
  };
};
